import { useState, useEffect, useCallback } from "react";
import { CloudOff, UploadCloud, Loader2, RefreshCw, AlertTriangle, Trash2, Check, Clock } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { listOutbox, deleteOutbox } from "../lib/offline/store";
import { syncNow, getSyncState } from "../lib/offline/sync";

const ENTITY = {
  patient: "Paciente",
  consultation: "Consulta",
  prescription: "Receta / documento",
  attachment: "Adjunto / nota",
  audit: "Registro de acceso",
};
const ACTION = { post: "Crear", put: "Editar", delete: "Eliminar" };

export default function SyncStatus() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [sync, setSync] = useState(getSyncState());

  const refresh = useCallback(async () => {
    setSync(getSyncState());
    try { setItems(await listOutbox()); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    refresh();
    const onChange = () => refresh();
    const onSynced = () => { refresh(); };
    const onOn = () => setOnline(true);
    const onOff = () => setOnline(false);
    window.addEventListener("clinped-offline-change", onChange);
    window.addEventListener("clinped-synced", onSynced);
    window.addEventListener("online", onOn);
    window.addEventListener("offline", onOff);
    return () => {
      window.removeEventListener("clinped-offline-change", onChange);
      window.removeEventListener("clinped-synced", onSynced);
      window.removeEventListener("online", onOn);
      window.removeEventListener("offline", onOff);
    };
  }, [refresh]);

  const pending = items.length;
  // Sin pendientes y con conexión: no mostramos nada (UI limpia).
  if (pending === 0 && online && !sync.syncing) return null;

  let label, cls, Icon, spin = false;
  if (sync.syncing) { label = "Sincronizando…"; cls = "bg-brand-sage/15 text-brand-sageDark"; Icon = Loader2; spin = true; }
  else if (pending > 0 && !online) { label = `${pending} sin subir`; cls = "bg-amber-100 text-amber-700"; Icon = CloudOff; }
  else if (pending > 0) { label = `${pending} por subir`; cls = "bg-amber-100 text-amber-700"; Icon = UploadCloud; }
  else { label = "Sin conexión"; cls = "bg-stone-100 text-brand-ink/60"; Icon = CloudOff; }

  const doSync = async () => {
    if (!navigator.onLine) { toast.error("Necesitas conexión para sincronizar."); return; }
    await syncNow();
    refresh();
    const n = (await listOutbox()).length;
    if (n === 0) toast.success("Todo sincronizado con la nube.");
  };

  const discard = async (id) => {
    if (!window.confirm("¿Descartar este cambio pendiente? No se subirá a la nube.")) return;
    await deleteOutbox(id);
    refresh();
  };

  return (
    <>
      <button
        data-testid="sync-status-btn"
        onClick={() => setOpen(true)}
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-2xl font-nunito font-bold text-xs transition-all hover:opacity-90 ${cls}`}>
        <Icon size={15} className={spin ? "animate-spin" : ""} />
        <span className="truncate">{label}</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-md max-h-[85vh] overflow-y-auto" data-testid="sync-panel">
          <DialogHeader>
            <DialogTitle className="font-outfit text-brand-ink flex items-center gap-2">
              <UploadCloud size={20} className="text-brand-terracotta" /> Sincronización
            </DialogTitle>
            <DialogDescription className="text-xs">Estado de tus cambios locales y su subida a la nube.</DialogDescription>
          </DialogHeader>

          <div className={`rounded-2xl px-4 py-3 text-sm font-semibold flex items-center gap-2 ${online ? "bg-brand-sage/10 text-brand-sageDark" : "bg-amber-50 text-amber-700"}`}>
            {online ? <Check size={16} /> : <CloudOff size={16} />}
            {online ? "Con conexión a internet" : "Sin conexión — los cambios se guardan en tu equipo"}
          </div>

          {sync.error && (
            <div className="rounded-2xl bg-destructive/10 text-destructive px-4 py-2.5 text-xs font-semibold flex items-center gap-2" data-testid="sync-error">
              <AlertTriangle size={15} /> {sync.error}
            </div>
          )}

          {pending === 0 ? (
            <div className="py-8 text-center" data-testid="sync-empty">
              <div className="w-12 h-12 rounded-2xl bg-brand-sage/15 flex items-center justify-center mx-auto mb-3"><Check className="text-brand-sageDark" size={24} /></div>
              <p className="font-outfit font-semibold text-brand-ink">Todo sincronizado</p>
              <p className="text-sm text-muted-foreground mt-1">No hay cambios pendientes de subir.</p>
            </div>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">{pending} cambio(s) esperando subir a la nube:</p>
              <div className="space-y-2" data-testid="sync-item-list">
                {items.map((it) => (
                  <div key={it.id} className="flex items-start gap-2 bg-stone-50 rounded-2xl px-3 py-2.5" data-testid={`sync-item-${it.id}`}>
                    <Clock size={15} className="text-brand-terracotta mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-brand-ink">{ACTION[it.method] || it.method} · {ENTITY[it.entity] || it.entity}</p>
                      {it.title && <p className="text-xs text-muted-foreground truncate">{it.title}</p>}
                      <p className="text-[11px] text-muted-foreground">{new Date(it.created_at).toLocaleString("es-MX")}</p>
                      {it.error && <p className="text-[11px] text-destructive font-semibold mt-0.5">⚠ {it.error}</p>}
                    </div>
                    <button data-testid={`sync-discard-${it.id}`} onClick={() => discard(it.id)} className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0" title="Descartar">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          <button
            data-testid="sync-now-btn"
            onClick={doSync}
            disabled={sync.syncing || !online || pending === 0}
            className="w-full flex items-center justify-center gap-2 bg-brand-terracotta hover:bg-brand-terracottaDark text-white font-outfit font-semibold py-3 rounded-2xl transition-all disabled:opacity-50 mt-1">
            {sync.syncing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} Sincronizar ahora
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
}
